import { BaseApiService } from '@doccareplus/api-client';
import type { AppointmentHit, InvoiceHit, PatientHit, StaffHit, VisitHit } from '../domain/searchResult';

/**
 * Global-search infrastructure — a thin HTTP adapter that reads the existing clinic
 * directory endpoints and returns the lean row shapes the domain works with.
 *
 * It deliberately hits the shared `/clinic/*` endpoints directly (patients, invoices,
 * staff, appointments) rather than importing other feature slices — mirroring how the
 * scheduling picker reuses `/clinic/patients` — so global-search stays self-contained and
 * introduces no cross-feature dependency.
 *
 * Model B header-based auth: the active clinic id / role are injected globally by
 * AuthSessionBridge, so no clinic id appears in any URL here.
 */

/** How many server-searched records to pull per source. Kept small — the palette shows a preview. */
export const SEARCH_PAGE_SIZE = 6;

interface Paginated<T> {
  items: T[];
}

/** Raw invoice list row (subset of Invoices.Common.InvoiceListItemResponse). */
interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  patientName: string;
  status: string;
}

/** Raw staff row (subset of Staff.Common.StaffMemberResponse). */
interface StaffRow {
  userId: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  roleNames: string[];
}

/** Raw appointment row (subset of Appointments.Common.AppointmentResponse). */
interface AppointmentRow {
  id: string;
  patientName?: string | null;
  tempPatientName?: string | null;
  tempPatientPhone?: string | null;
  status: string;
  reason?: string | null;
  startTime: string;
}

class GlobalSearchApiService extends BaseApiService {
  constructor() {
    super('/clinic');
  }

  /** Server-side patient search (name / patient number / exact phone / exact email). */
  async searchPatients(search: string): Promise<PatientHit[]> {
    const page = await this.get<Paginated<PatientHit>>('/patients', {
      params: { search, page: 1, pageSize: SEARCH_PAGE_SIZE },
    });
    return page.items;
  }

  /** Server-side invoice search (invoice number / patient name). */
  async searchInvoices(search: string): Promise<InvoiceHit[]> {
    const page = await this.get<Paginated<InvoiceRow>>('/invoices', {
      params: { search, page: 1, pageSize: SEARCH_PAGE_SIZE },
    });
    return page.items.map((row) => ({
      id: row.id,
      invoiceNumber: row.invoiceNumber,
      patientName: row.patientName,
      status: row.status,
    }));
  }

  /** Full staff list — the clinic staff endpoint returns every member unpaginated for client-side filtering. */
  async listStaff(): Promise<StaffHit[]> {
    const rows = await this.get<StaffRow[]>('/staff');
    return rows.map((row) => ({
      userId: row.userId,
      fullName: row.fullName,
      email: row.email,
      phone: row.phone,
      roleNames: row.roleNames,
    }));
  }

  /**
   * Appointments in a date window for client-side filtering. The endpoint has no free-text
   * search and caps the range, so the caller passes a bounded `[from, to)` window and filters
   * the returned rows by patient name/phone in the domain.
   */
  async listAppointments(from: string, to: string): Promise<AppointmentHit[]> {
    const rows = await this.get<AppointmentRow[]>('/appointments', { params: { from, to } });
    return rows.map((row) => ({
      id: row.id,
      patientName: row.patientName,
      tempPatientName: row.tempPatientName,
      tempPatientPhone: row.tempPatientPhone,
      status: row.status,
      reason: row.reason,
      startTime: row.startTime,
    }));
  }
  searchVisits(search: string): Promise<VisitHit[]> { return this.get<VisitHit[]>('/visits/search', { params: { search, limit: SEARCH_PAGE_SIZE } }); }
}

export const globalSearchApi = new GlobalSearchApiService();
